import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Tree,
  Typography,
  type TreeDataNode,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@mall/shared';
import { useToast } from '../../app/ToastProvider';
import { createCategory, deleteCategory, listCategories, updateCategory } from './api';
import {
  categoryDisplayName,
  flattenForPicker,
  type CategoryUpsertRequest,
  type CategoryView,
} from './types';

interface FormValues {
  parentId: number | null;
  name: string;
  nameCn: string;
  sortOrder: number;
}

const ROOT_PARENT_VALUE = 0;

interface DialogState {
  mode: 'create-root' | 'create-child' | 'edit';
  /** for create-child: the parent. for edit: the record being edited. */
  context?: CategoryView;
}

interface CategoryTreeNode extends TreeDataNode {
  record: CategoryView;
  children: CategoryTreeNode[];
}

function buildTree(rows: CategoryView[]): CategoryTreeNode[] {
  const byParent = new Map<number, CategoryView[]>();
  for (const row of rows) {
    const key = row.parentId ?? 0;
    const list = byParent.get(key) ?? [];
    list.push(row);
    byParent.set(key, list);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id);
  }
  const build = (parentId: number): CategoryTreeNode[] =>
    (byParent.get(parentId) ?? []).map((row) => ({
      key: row.id,
      title: categoryDisplayName(row),
      record: row,
      children: build(row.id),
    }));
  return build(0);
}

function hasChildren(rows: CategoryView[], id: number): boolean {
  return rows.some((r) => r.parentId === id);
}

export function CategoriesPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['operator', 'categories'], queryFn: listCategories });
  const rows = useMemo<CategoryView[]>(() => query.data ?? [], [query.data]);
  const tree = useMemo(() => buildTree(rows), [rows]);
  const flatOptions = useMemo(() => flattenForPicker(rows), [rows]);

  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [form] = Form.useForm<FormValues>();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['operator', 'categories'] });

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: CategoryUpsertRequest = {
        parentId: values.parentId && values.parentId !== ROOT_PARENT_VALUE ? values.parentId : null,
        name: values.name.trim(),
        name_cn: values.nameCn?.trim() || values.name.trim(),
        sortOrder: values.sortOrder ?? 0,
      };
      if (dialog?.mode === 'edit' && dialog.context) {
        return updateCategory(dialog.context.id, payload);
      }
      return createCategory(payload);
    },
    onSuccess: () => {
      toast.success(dialog?.mode === 'edit' ? '已更新' : '已创建');
      setDialog(null);
      void invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '保存失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCategory(id),
    onSuccess: () => {
      toast.success('已删除');
      void invalidate();
    },
    onError: (err: Error) => {
      toast.error(err instanceof ApiError ? err.message : '删除失败');
    },
  });

  const openCreateRoot = () => {
    setDialog({ mode: 'create-root' });
    form.setFieldsValue({ parentId: ROOT_PARENT_VALUE, name: '', nameCn: '', sortOrder: 0 });
  };
  const openCreateChild = (parent: CategoryView) => {
    setDialog({ mode: 'create-child', context: parent });
    form.setFieldsValue({ parentId: parent.id, name: '', nameCn: '', sortOrder: 0 });
  };
  const openEdit = (record: CategoryView) => {
    setDialog({ mode: 'edit', context: record });
    form.setFieldsValue({
      parentId: record.parentId ?? ROOT_PARENT_VALUE,
      name: record.name,
      nameCn: categoryDisplayName(record),
      sortOrder: record.sortOrder ?? 0,
    });
  };

  const dialogTitle = useMemo(() => {
    if (!dialog) return '';
    if (dialog.mode === 'edit') {
      return `编辑分类 #${dialog.context?.id}`;
    }
    if (dialog.mode === 'create-child') {
      return `在「${dialog.context ? categoryDisplayName(dialog.context) : ''}」下新建子分类`;
    }
    return '新建一级分类';
  }, [dialog]);

  // when editing, exclude self + descendants from parent picker (no cycles)
  const disabledIds = useMemo(() => {
    if (dialog?.mode !== 'edit' || !dialog.context) return new Set<number>();
    const editingId = dialog.context.id;
    const out = new Set<number>([editingId]);
    const expand = (parentId: number) => {
      for (const opt of flatOptions) {
        if (opt.parentId === parentId && !out.has(opt.id)) {
          out.add(opt.id);
          expand(opt.id);
        }
      }
    };
    expand(editingId);
    return out;
  }, [dialog, flatOptions]);

  return (
    <div className="mall-page">
      <div className="mall-page-header">
        <div>
          <div className="mall-page-kicker">Global Categories</div>
          <Typography.Title level={3} className="mall-page-title">
            全局分类
          </Typography.Title>
          <p className="mall-page-description">维护所有租户共用的分类树、中文名称和排序。</p>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => query.refetch()}
            loading={query.isFetching}
          >
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRoot}>
            新建一级分类
          </Button>
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        message="分类是平台级（全局）数据"
        description="所有租户共用同一棵分类树。租户管理员在 admin 端只读，新增 / 修改 / 删除均在此处完成。"
      />

      <Card loading={query.isLoading} className="mall-surface">
        <div className="mall-table-toolbar" style={{ margin: '-18px -18px 14px' }}>
          <div>
            <div className="mall-table-title">分类树</div>
            <div className="mall-table-subtitle">共 {rows.length} 个全局分类</div>
          </div>
        </div>
        {tree.length === 0 && !query.isLoading && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            暂无分类，点击右上角「新建一级分类」开始维护。
          </Typography.Paragraph>
        )}
        {tree.length > 0 && (
          <Tree
            className="mall-tree-list"
            treeData={tree}
            defaultExpandAll
            showLine
            blockNode
            selectable={false}
            titleRender={(node) => {
              const row = (node as CategoryTreeNode).record;
              const childExists = hasChildren(rows, row.id);
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    paddingRight: 4,
                  }}
                >
                  <Space size={8}>
                    <Typography.Text strong>{categoryDisplayName(row)}</Typography.Text>
                    <Tag>#{row.id}</Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      排序 {row.sortOrder ?? 0}
                    </Typography.Text>
                  </Space>
                  <Space size={4}>
                    <Tooltip title="新建子分类">
                      <Button
                        size="small"
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => openCreateChild(row)}
                      />
                    </Tooltip>
                    <Tooltip title="编辑">
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(row)}
                      />
                    </Tooltip>
                    <Tooltip title={childExists ? '请先删除子分类' : '删除'}>
                      <Popconfirm
                        title={`确认删除分类「${categoryDisplayName(row)}」？`}
                        okText="删除"
                        okButtonProps={{ danger: true }}
                        disabled={childExists}
                        onConfirm={() => deleteMutation.mutate(row.id)}
                      >
                        <Button
                          size="small"
                          type="text"
                          danger
                          disabled={childExists}
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Tooltip>
                  </Space>
                </div>
              );
            }}
            style={{ background: 'transparent' }}
          />
        )}
        {query.isError && (
          <Typography.Paragraph type="danger" style={{ marginTop: 16 }}>
            加载失败：{query.error instanceof Error ? query.error.message : '未知错误'}
          </Typography.Paragraph>
        )}
      </Card>

      <Modal
        title={dialogTitle}
        open={!!dialog}
        onCancel={() => setDialog(null)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        destroyOnClose
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveMutation.mutate(values)}
        >
          <Form.Item label="父分类" name="parentId">
            <Select<number>
              showSearch
              optionFilterProp="label"
              options={[
                { value: ROOT_PARENT_VALUE, label: '— 无（作为一级分类） —' },
                ...flatOptions.map((opt) => ({
                  value: opt.id,
                  label: opt.label,
                  disabled: disabledIds.has(opt.id),
                })),
              ]}
            />
          </Form.Item>
          <Form.Item
            label="分类名称（英文/通用）"
            name="name"
            rules={[{ required: true, message: '名称必填' }]}
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            label="分类中文名称"
            name="nameCn"
            tooltip="接口返回字段 name_cn；不填则默认使用上面的分类名称"
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item label="排序" name="sortOrder" tooltip="数字越小越靠前；同级分类按升序展示">
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
